import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useApi } from '../hooks/useApi';

interface PickListItem {
  id: string;
  sku: string;
  title: string;
  quantity: number;
  pickedQuantity: number;
  locationCode: string;
  orderNumber: string;
}

interface PickListData {
  id: string;
  reference: string;
  status: string;
  items: PickListItem[];
}

export default function PickList() {
  const { pickListId } = useParams();
  const navigate = useNavigate();
  const { fetchApi, loading, error: apiError } = useApi();
  const [pickList, setPickList] = useState<PickListData | null>(null);
  const [filter, setFilter] = useState<'all' | 'pending' | 'picked'>('all');

  useEffect(() => {
    loadPickList();
  }, [pickListId]);

  async function loadPickList() {
    const data = await fetchApi<{ pickList: PickListData }>(`/picking/lists/${pickListId}`);
    if (data?.pickList) {
      setPickList(data.pickList);
    }
  }

  const handleCompletePickList = async () => {
    if (!confirm('Complete this pick list?')) return;

    const result = await fetchApi(`/picking/lists/${pickListId}/complete`, {
      method: 'POST',
    });

    if (result) navigate('/');
  };

  if (loading || !pickList) {
    return (
      <div className="page">
        <div className="loading-screen">
          <div className="spinner" />
        </div>
      </div>
    );
  }

  const filteredItems = pickList.items.filter((item) => {
    if (filter === 'pending') return item.pickedQuantity < item.quantity;
    if (filter === 'picked') return item.pickedQuantity >= item.quantity;
    return true;
  });

  const totalItems = pickList.items.length;
  const pickedItems = pickList.items.filter((i) => i.pickedQuantity >= i.quantity).length;
  const progress = totalItems > 0 ? (pickedItems / totalItems) * 100 : 0;

  return (
    <div className="page">
      <div className="page-header">
        <button
          onClick={() => navigate('/')}
          style={{ background: 'none', border: 'none', fontSize: '1.5rem' }}
        >
          ←
        </button>
        <h1>{pickList.reference}</h1>
        <span className={`badge badge-${pickList.status === 'completed' ? 'success' : 'info'}`}>
          {pickList.status}
        </span>
      </div>

      <div className="page-content">
        {apiError && <p role="alert" style={{ color: 'var(--danger)' }}>{apiError}</p>}
        {/* Progress */}
        <div className="card">
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
            <span>Progress</span>
            <span style={{ fontWeight: 'bold' }}>{pickedItems}/{totalItems}</span>
          </div>
          <div className="progress-bar">
            <div className="progress-bar-fill" style={{ width: `${progress}%` }} />
          </div>
        </div>

        {/* Filter Tabs */}
        <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1rem' }}>
          {(['all', 'pending', 'picked'] as const).map((f) => (
            <button
              key={f}
              className={`btn ${filter === f ? 'btn-primary' : 'btn-secondary'}`}
              style={{ flex: 1, padding: '0.5rem' }}
              onClick={() => setFilter(f)}
            >
              {f.charAt(0).toUpperCase() + f.slice(1)}
            </button>
          ))}
        </div>

        {/* Items */}
        {filteredItems.map((item) => {
          const isPicked = item.pickedQuantity >= item.quantity;

          return (
            <div
              key={item.id}
              className="list-item"
              onClick={() => navigate(`/pick/${pickListId}/item/${item.id}`)}
              style={{
                opacity: isPicked ? 0.6 : 1,
                borderLeft: isPicked ? '4px solid var(--success)' : 'none',
              }}
            >
              <div className="list-item-content">
                <div className="list-item-title">{item.sku}</div>
                <div className="list-item-subtitle">
                  {item.title.length > 40 ? item.title.substring(0, 40) + '...' : item.title}
                </div>
                <div style={{ marginTop: '0.25rem', fontSize: '0.75rem', color: 'var(--gray-500)' }}>
                  📍 {item.locationCode} • Order #{item.orderNumber}
                </div>
              </div>
              <div style={{ textAlign: 'right' }}>
                <div style={{ fontSize: '1.25rem', fontWeight: 'bold' }}>
                  {item.pickedQuantity}/{item.quantity}
                </div>
                {isPicked && <span className="badge badge-success">✓</span>}
              </div>
            </div>
          );
        })}

        {filteredItems.length === 0 && (
          <div className="card" style={{ textAlign: 'center', color: 'var(--gray-500)' }}>
            {filter === 'pending' ? 'All items picked!' : 'No items found'}
          </div>
        )}
      </div>

      {/* Bottom Actions */}
      <div className="bottom-actions">
        <button
          className="btn btn-secondary"
          style={{ flex: 1 }}
          onClick={() => navigate('/scanner')}
        >
          📷 Scan
        </button>
        {progress === 100 && (
          <button
            className="btn btn-success"
            style={{ flex: 1 }}
            onClick={handleCompletePickList}
          >
            ✓ Complete
          </button>
        )}
      </div>
    </div>
  );
}
